import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { ModalPasswordConfirmation, UiSelect } from 'components/common'
import { Button, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, useDisclosure, VStack } from '@chakra-ui/react'
import { createProjectShippingPO, deleteProjectShippingPO, getEmcVendors, getFabricationVendors, getProjectShipToByProjectId, getShippingVendors, updateProjectShippingPO } from 'api'
import { useImmer } from 'use-immer'
import { useAppContext } from 'hooks/useAppContext'
import { format as formatDate } from 'date-fns'
import { getAssetUrl, PROJECT_SHIPPING_PO_CATEGORIES } from 'lib'

const initialForm = {
  name: '',
  shipperCompany: '',
  shipfromCompany: '',
  shipfromAddress: '',
  shipfromCity: '',
  shipfromState: '',
  shipfromZip: '',
  shiptoCompany: '',
  shiptoAddress: '',
  shiptoCity: '',
  shiptoState: '',
  shiptoZip: '',
  price: 0,
  pro: '',
  bol: null,
  eta: null
}

export const ModalProjectShippingPOFormType = {
  Add: 'Add',
  Edit: 'Edit'
}

export const ModalProjectShippingPOForm = ({ isOpen, onClose, type = ModalProjectShippingPOFormType.Add, po, projectId, reload }) => {
  const { createToast } = useAppContext()
  const [isLocked, setIsLocked] = useState(true)
  const [form, setForm] = useImmer(initialForm)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedShipper, setSelectedShipper] = useState(null)
  const [selectedShipfrom, setSelectedShipfrom] = useState(null)
  const disclosureRemovePO = useDisclosure()

  useEffect(() => {
    const load = async () => {
      if (type === ModalProjectShippingPOFormType.Add || !po) {
        setForm(initialForm)
        setSelectedShipper(null)
        setSelectedShipfrom(null)
        try {
          const data = await getProjectShipToByProjectId(projectId)
          setForm(draft => {
            draft.shiptoCompany = data?.company || ''
            draft.shiptoAddress = data?.address || ''
            draft.shiptoCity = data?.city || ''
            draft.shiptoState = data?.state || ''
            draft.shiptoZip = data?.zip || ''
          })
        } catch (err) {
          setForm(draft => {
            draft.shiptoCompany = ''
            draft.shiptoAddress = ''
            draft.shiptoCity = ''
            draft.shiptoState = ''
            draft.shiptoZip = ''
          })
        }
      } else {
        setForm(draft => {
          Object.keys(po || {}).forEach(k => {
            if (k !== 'id' && k !== 'projectId' && k !== 'bol' && k !== 'pdf' && k !== 'createdAt' && k !== 'updatedAt') {
              draft[k] = po[k] ?? ''
            }
          })
        })
        setSelectedShipper({
          name: po.shipperCompany || ''
        })
        setSelectedShipfrom({
          name: po.shipfromCompany || '',
          address: po.shipfromAddress || '',
          city: po.shipfromCity || '',
          state: po.shipfromState || '',
          zip: po.shipfromZip || ''
        })
      }
    }
    load()
  }, [type, po, isOpen])

  const handleSave = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      let response
      if (type === ModalProjectShippingPOFormType.Add) {
        response = await createProjectShippingPO({ ...form, price: form.price * 1, projectId })
      } else {
        response = await updateProjectShippingPO(po.id, { ...form, price: form.price * 1 })
      }
      window.open(getAssetUrl(response.pdf))
      createToast('success', 'Success', 'Po saved.')
      setIsSaving(false)
      onClose()
      reload()
      setSelectedShipper(null)
      setSelectedShipfrom(null)
      setForm(initialForm)
    } catch (err) {
      setIsSaving(false)
      createToast('error', 'Error', err)
    }
  }

  const handleRemove = async (id) => {
    try {
      await deleteProjectShippingPO(id)
      disclosureRemovePO.onClose()
      onClose()
      reload()
      createToast('success', 'Success', 'Po removed.')
    } catch {
      createToast('error', 'Error', 'Failed to remove the PO.')
    }
  }

  const handleChange = (e) => {
    setForm(draft => { draft[e.target.name] = e.target.value })
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]

    if (file) {
      setForm(draft => {
        draft[e.target.name] = e.target.files[0]
      })
    }
  }

  const eta = useMemo(() => {
    const date = new Date(form.eta)
    return form.eta ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'yyyy-MM-dd') : ''
  }, [form.eta])

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSave}>
            <ModalHeader>Ship Item</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack alignItems="none" gap={4}>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Item Name</FormLabel>
                      <Select name="name" value={form.name} onChange={handleChange} placeholder="Select">
                        {Object.keys(PROJECT_SHIPPING_PO_CATEGORIES).map(v =>
                          <option key={PROJECT_SHIPPING_PO_CATEGORIES[v]} value={PROJECT_SHIPPING_PO_CATEGORIES[v]}>{PROJECT_SHIPPING_PO_CATEGORIES[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
                <Heading size="sm">Carrier</Heading>
                <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Company</FormLabel>
                      <UiSelect
                        value={selectedShipper}
                        onChange={v => {
                          setSelectedShipper(v)
                          setForm(draft => {
                            draft.shipperCompany = v?.name || ''
                          })
                        }}
                        loadData={(page, search) => {
                          return getShippingVendors(page, 20, search, 'name', 'asc')
                        }}
                        apiRespProp="rows"
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.name}
                        placeholder="Search"
                        noOptionsMessage={() => 'Not Found'}
                        required={true}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
                <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                  <GridItem>
                    <Heading size="sm" mb={2}>Ship From</Heading>
                    <FormControl isRequired>
                      <FormLabel>Company</FormLabel>
                      <UiSelect
                        value={selectedShipfrom}
                        onChange={v => {
                          setSelectedShipfrom(v)
                          setForm(draft => {
                            draft.shipfromCompany = v?.name || ''
                            draft.shipfromAddress = v?.address || ''
                            draft.shipfromCity = v?.city || ''
                            draft.shipfromState = v?.state || ''
                            draft.shipfromZip = v?.zip || ''
                          })
                        }}
                        loadData={(page, search) => {
                          return Promise.all([
                            getEmcVendors(page, 20, search, 'name', 'asc'),
                            getFabricationVendors(page, 20, search, 'name', 'asc')
                          ])
                            .then(([respEmcVendors, respFabricationVendors]) => {
                              // Merge the results into a single array
                              const mergedResult = [...respEmcVendors.rows, ...respFabricationVendors.rows]
                              return { rows: mergedResult }
                            })
                        }}
                        apiRespProp="rows"
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.name}
                        placeholder="Search"
                        noOptionsMessage={() => 'Not Found'}
                        required={true}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Address</FormLabel>
                      <Input type="text" name="shipfromAddress" value={form.shipfromAddress} onChange={handleChange} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>City</FormLabel>
                      <Input type="text" name="shipfromCity" value={form.shipfromCity} onChange={handleChange} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>State</FormLabel>
                      <Input type="text" name="shipfromState" value={form.shipfromState} onChange={handleChange} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Zip</FormLabel>
                      <Input type="text" name="shipfromZip" value={form.shipfromZip} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <Heading size="sm" mb={2}>Ship To</Heading>
                    <FormControl>
                      <FormLabel>Company</FormLabel>
                      <Input type="text" name="shiptoCompany" value={form.shiptoCompany} onChange={handleChange} isReadOnly={isLocked} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Address</FormLabel>
                      <Input type="text" name="shiptoAddress" value={form.shiptoAddress} onChange={handleChange} isReadOnly={isLocked} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>City</FormLabel>
                      <Input type="text" name="shiptoCity" value={form.shiptoCity} onChange={handleChange} isReadOnly={isLocked} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>State</FormLabel>
                      <Input type="text" name="shiptoState" value={form.shiptoState} onChange={handleChange} isReadOnly={isLocked} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Zip</FormLabel>
                      <Input type="text" name="shiptoZip" value={form.shiptoZip} onChange={handleChange} isReadOnly={isLocked} />
                    </FormControl>
                    <Flex justifyContent="flex-end" mt={2}>
                      <Button onClick={() => setIsLocked(!isLocked)}>{isLocked ? 'Unlock' : 'Lock'} Inputs</Button>
                    </Flex>
                  </GridItem>
                </Grid>
                <Heading size="sm">Extra</Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Price</FormLabel>
                      <Input
                        type="number"
                        value={form.price}
                        onChange={e => {
                          setForm(draft => {
                            draft.price = e.target.value
                          })
                        }}
                        onBlur={e => {
                          setForm(draft => {
                            draft.price = parseFloat(e.target.value || 0).toFixed(2)
                          })
                        }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Tracking No</FormLabel>
                      <Input type="text" name="pro" value={form.pro} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormLabel>BOL</FormLabel>
                    <Input type="file" name="bol" onChange={handleFileChange} />
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>ETA</FormLabel>
                      <Input type="date" name="eta" value={eta} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                </Grid>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Flex justifyContent="flex-end" gap={4}>
                {type === ModalProjectShippingPOFormType.Edit && (
                  <Button type="button" colorScheme="red" onClick={() => disclosureRemovePO.onOpen()}>
                    Delete
                  </Button>
                )}
                <Button type="submit" colorScheme="blue" isLoading={isSaving}>
                  {type === ModalProjectShippingPOFormType.Add ? 'Add' : 'Save'}
                </Button>
                <Button onClick={onClose}>Close</Button>
              </Flex>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      {disclosureRemovePO.isOpen && (
        <ModalPasswordConfirmation
          heading="Are you sure to remove the PO?"
          btnConfirmLabel="Remove"
          onConfirm={() => handleRemove(po.id)}
          {...disclosureRemovePO}
        />
      )}
    </>
  )
}

ModalProjectShippingPOForm.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  type: PropTypes.oneOf(Object.values(ModalProjectShippingPOFormType)),
  po: PropTypes.object,
  projectId: PropTypes.number,
  reload: PropTypes.func
}
